import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wind } from "lucide-react";
import { burst, fireworks } from "@/lib/confetti";
import { sound } from "@/lib/audio";

const STATUS_LINES = [
    ["Birthday status", "LEGENDARY"],
    ["Website status", "IN GOOD HANDS"],
    ["Developer status", "GRATEFUL"],
    ["Next maintenance window", "NOT TODAY. IT'S YOUR BIRTHDAY!"],
];

const FLOATERS = ["Make a wish", "Certified fabulous", "Level up achieved", "One of a kind"];

const Finale = forwardRef(function Finale({ reduce }, ref) {
    const [lit, setLit] = useState([true, true, true, true, true]);
    const [smoke, setSmoke] = useState(() => new Set());
    const [celebrated, setCelebrated] = useState(false);
    const allOut = lit.every((l) => !l);

    const extinguish = (i) => {
        if (!lit[i]) return;
        sound.pop();
        setLit((prev) => prev.map((l, idx) => (idx === i ? false : l)));
        setSmoke((prev) => new Set(prev).add(i));
        setTimeout(() => setSmoke((prev) => { const n = new Set(prev); n.delete(i); return n; }), 1500);
    };

    const blowAll = () => {
        lit.forEach((l, i) => { if (l) setTimeout(() => extinguish(i), i * 180); });
    };

    useEffect(() => {
        if (allOut && !celebrated) {
            setCelebrated(true);
            sound.applause();
            sound.chime(1046.5);
            fireworks(6500);
            const w = window.innerWidth;
            burst({ x: w * 0.5, y: window.innerHeight * 0.35, count: 130 });
            setTimeout(() => burst({ x: w * 0.2, y: window.innerHeight * 0.4, count: 60 }), 400);
            setTimeout(() => burst({ x: w * 0.8, y: window.innerHeight * 0.4, count: 60 }), 700);
        }
    }, [allOut, celebrated]);

    useImperativeHandle(ref, () => ({
        relight() {
            setCelebrated(false);
            setLit([true, true, true, true, true]);
        },
    }));

    return (
        <section
            id="finale"
            data-testid="finale-section"
            className="relative z-[1] flex min-h-[100svh] flex-col items-center justify-center overflow-hidden px-6 py-24 text-center"
        >
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                {FLOATERS.map((f, i) => (
                    <motion.span
                        key={f}
                        className="absolute hidden rounded-full border border-[#F3D085]/25 bg-[#160E2A]/70 px-4 py-1.5 font-mono2 text-[9px] uppercase tracking-[0.25em] text-[#F3D085]/70 backdrop-blur-sm md:block"
                        style={{ left: `${10 + i * 24}%`, top: `${14 + (i % 2) * 12}%` }}
                        animate={reduce ? {} : { y: [0, -16, 0] }}
                        transition={{ repeat: Infinity, duration: 5 + i, ease: "easeInOut" }}
                    >
                        {f}
                    </motion.span>
                ))}
            </div>

            <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
                <p className="eyebrow-label mb-4">Act V — The Grand Finale</p>
                <h2 className="font-display text-3xl font-semibold sm:text-5xl">
                    Make a wish, <span className="gold-text italic">Mary</span>
                </h2>
                <p className="mx-auto mt-3 max-w-sm text-sm font-light text-[#EDE7F8]/70">
                    Tap each candle — or blow them all out at once. The universe is listening.
                </p>
            </motion.div>

            <motion.div
                className="relative mt-20"
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
                <div className="relative mx-auto flex h-14 w-44 items-end justify-around px-3 sm:w-52">
                    {lit.map((isLit, i) => (
                        <button
                            key={i}
                            data-testid={`candle-${i + 1}`}
                            aria-label={isLit ? `Blow out candle ${i + 1}` : `Candle ${i + 1} is out`}
                            onClick={() => extinguish(i)}
                            className="relative h-12 w-2.5 cursor-pointer rounded-sm bg-gradient-to-b from-[#ffe9b3] via-[#F3D085] to-[#C59B27]"
                        >
                            {isLit && <span className="flame" aria-hidden="true" />}
                            {smoke.has(i) && <span className="smoke" aria-hidden="true" />}
                        </button>
                    ))}
                </div>

                <div className="mx-auto -mt-1 h-16 w-52 rounded-t-xl border border-[#F3D085]/50 bg-gradient-to-b from-[#3a1f4e] to-[#241233] sm:w-60">
                    <div className="mx-auto mt-3 h-px w-4/5 bg-[#F3D085]/40" />
                </div>
                <div className="mx-auto h-[4.5rem] w-72 rounded-t-xl border border-[#F3D085]/40 bg-gradient-to-b from-[#2A1438] to-[#1a0e26] sm:w-80">
                    <div className="mx-auto mt-3 h-px w-4/5 bg-[#F3D085]/30" />
                </div>
                <div className="mx-auto h-20 w-[21rem] max-w-[86vw] rounded-t-xl border border-[#F3D085]/30 bg-gradient-to-b from-[#20102e] to-[#120a1c]">
                    <div className="mx-auto mt-3 h-px w-4/5 bg-[#F3D085]/25" />
                </div>
                <div className="mx-auto mt-2 h-3 w-[24rem] max-w-[94vw] rounded-full bg-gradient-to-r from-transparent via-[#C59B27]/70 to-transparent" />
            </motion.div>

            <button
                data-testid="extinguish-candles-button"
                onClick={blowAll}
                disabled={allOut}
                className="btn-ghost mt-10 disabled:cursor-default disabled:opacity-40"
            >
                <Wind className="h-4 w-4" />
                {allOut ? "All wishes made" : "Blow out the candles"}
            </button>

            <AnimatePresence>
                {celebrated && (
                    <motion.div
                        data-testid="finale-celebration"
                        className="mt-16"
                        initial={{ opacity: 0, scale: 0.92, y: 30 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <h3 className="gold-text font-display text-4xl font-bold italic leading-tight sm:text-6xl lg:text-7xl">
                            Happy Birthday,<br />Mary Manzur!
                        </h3>
                        <p className="mt-5 font-display text-xl font-light italic text-[#EDE7F8]/90 sm:text-2xl">
                            May your next chapter be your most beautiful one yet.
                        </p>
                        <p className="mt-3 text-sm font-light tracking-wide text-[#C4B5FD] sm:text-base">
                            More joy. More abundance. More unforgettable moments.
                        </p>

                        <div className="mx-auto mt-10 max-w-md space-y-2.5 rounded-2xl border border-[#F3D085]/20 bg-[#0B0716]/70 p-6 text-left backdrop-blur">
                            {STATUS_LINES.map(([k, v], i) => (
                                <motion.p
                                    key={k}
                                    className="font-mono2 text-[11px] tracking-[0.14em] sm:text-xs"
                                    initial={{ opacity: 0, x: -14 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.5 + i * 0.25, duration: 0.5 }}
                                >
                                    <span className="text-[#C4B5FD]/70">{k}:</span>{" "}
                                    <span className="font-bold text-[#FFD700]">{v}</span>
                                </motion.p>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
});

export default Finale;
